import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import Button from '../../components/common/Button'
import { subscriptionsApi } from '../../services/api'
import { saveSelectedCheckoutPlan } from '../../utils/checkoutPlan'
import { RootState } from '../../store/store'
import { evaluatePurchaseAccess } from '../../utils/accessFlow'

import { SharedPlanCard, Plan, BillingPeriod, BillingToggle } from '../../components/subscription/SharedPlanCard'

// The admin marks one plan per billing period as "Most Popular". Since we pass
// only the filtered plans for the current period, this resolves to at most one.
const resolvePopularId = (plans: Plan[]): number | null => {
  const flagged = plans.find(p => p.is_popular)
  return flagged ? flagged.id : null
}

/* ── billing periods ──────────────────────────────────────────────────── */

const BILLING_ORDER: BillingPeriod[] = ['monthly', 'quarterly', 'yearly']

/** Put the "Most Popular" plan in the centre column so it anchors the grid. */
function centerPopularPlan(plans: Plan[]): Plan[] {
  if (plans.length < 3) return plans
  const popularIndex = plans.findIndex(plan => plan.is_popular)
  const middle = Math.floor(plans.length / 2)
  if (popularIndex === -1 || popularIndex === middle) return plans

  const reordered = plans.filter((_, i) => i !== popularIndex)
  reordered.splice(middle, 0, plans[popularIndex])
  return reordered
}


const MobileCarousel: React.FC<{ plans: Plan[] }> = ({ plans }) => {
  const [current, setCurrent] = useState(0)
  const scrollRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { user } = useSelector((state: RootState) => state.auth)
  const popularId = resolvePopularId(plans)

  const handleGetStarted = async (plan: Plan) => {
    saveSelectedCheckoutPlan(plan.slug)
    const next = `/dashboard/checkout?planSlug=${plan.slug}`
    const access = await evaluatePurchaseAccess(user, { next })
    navigate(access.allowed ? next : (access.redirectTo || '/login'))
  }

  const scrollTo = (index: number) => {
    const next = Math.max(0, Math.min(index, plans.length - 1))
    setCurrent(next)
    const container = scrollRef.current
    if (!container) return
    const card = container.children[next] as HTMLElement
    if (card) card.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
  }

  const handleScroll = () => {
    const container = scrollRef.current
    if (!container) return
    const cardWidth = (container.children[0] as HTMLElement)?.offsetWidth || 1
    const idx = Math.round(container.scrollLeft / (cardWidth + 20))
    setCurrent(Math.max(0, Math.min(idx, plans.length - 1)))
  }

  return (
    <div className="md:hidden mb-16">
      {/* Arrow nav */}
      <div className="flex items-center justify-between px-4 mb-3">
        <button
          type="button"
          onClick={() => scrollTo(current - 1)}
          disabled={current === 0}
          className="w-9 h-9 rounded-full bg-dark-700 border border-dark-600 flex items-center justify-center text-gray-300 hover:bg-dark-600 disabled:opacity-30 transition-all"
        >
          <ChevronLeftIcon className="w-5 h-5" />
        </button>
        <span className="text-xs text-gray-500">{current + 1} / {plans.length}</span>
        <button
          type="button"
          onClick={() => scrollTo(current + 1)}
          disabled={current === plans.length - 1}
          className="w-9 h-9 rounded-full bg-dark-700 border border-dark-600 flex items-center justify-center text-gray-300 hover:bg-dark-600 disabled:opacity-30 transition-all"
        >
          <ChevronRightIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Cards track */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex overflow-x-auto snap-x snap-mandatory gap-5 px-6 pb-4"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {plans.map(plan => (
          <div key={plan.id} className="flex-shrink-0 w-[calc(100vw-3rem)] snap-center">
            <SharedPlanCard plan={plan} popular={plan.id === popularId} onGetStarted={handleGetStarted} />
          </div>
        ))}
      </div>

      {/* Dot indicators */}
      <div className="flex justify-center gap-2 mt-3">
        {plans.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => scrollTo(i)}
            className={`transition-all duration-200 rounded-full ${i === current ? 'w-5 h-2 bg-primary-400' : 'w-2 h-2 bg-dark-600 hover:bg-dark-500'}`}
          />
        ))}
      </div>
    </div>
  )
}

const PricingPage: React.FC = () => {
  const [plans, setPlans] = useState<Plan[]>([])
  const [loading, setLoading] = useState(true)
  const [faqs, setFaqs] = useState<{ id: number; question: string; answer: string }[]>([])
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0)
  const [billing, setBilling] = useState<BillingPeriod>('monthly')

  const navigate = useNavigate()
  const { user } = useSelector((state: RootState) => state.auth)

  const handleGetStarted = async (plan: Plan) => {
    saveSelectedCheckoutPlan(plan.slug)
    const next = `/dashboard/checkout?planSlug=${plan.slug}`
    const access = await evaluatePurchaseAccess(user, { next })
    navigate(access.allowed ? next : (access.redirectTo || '/login'))
  }

  useEffect(() => {
    subscriptionsApi.getPlans(true)
      .then(setPlans)
      .catch(() => {})
      .finally(() => setLoading(false))
    subscriptionsApi.getFaqs()
      .then(setFaqs)
      .catch(() => {})
  }, [])

  // Only offer a billing period the admin has actually created plans for,
  // otherwise the toggle leads to an empty grid.
  const availablePeriods = useMemo(
    () => BILLING_ORDER.filter(period => plans.some(plan => plan.billing_period === period)),
    [plans],
  )

  useEffect(() => {
    if (availablePeriods.length > 0 && !availablePeriods.includes(billing)) {
      setBilling(availablePeriods[0])
    }
  }, [availablePeriods, billing])

  const visiblePlans = useMemo(
    () => centerPopularPlan(plans.filter(plan => plan.billing_period === billing)),
    [plans, billing],
  )

  return (
    <div className="min-h-screen bg-dark-900 text-white py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-amber-400 to-primary-400 bg-clip-text text-transparent">
            Choose Your Advisory Plan
          </h1>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            SEBI-registered research-backed recommendations with disciplined risk management
          </p>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Empty */}
        {!loading && plans.length === 0 && (
          <div className="text-center py-20 text-gray-400">
            <p className="text-lg">No plans available yet. Check back soon.</p>
          </div>
        )}

        {/* Billing toggle — hidden when only one period exists */}
        {!loading && availablePeriods.length > 1 && (
          <div className="flex justify-center mb-10">
            <BillingToggle value={billing} periods={availablePeriods} onChange={setBilling} />
          </div>
        )}

        {/* Desktop grid */}
        {!loading && visiblePlans.length > 0 && (
          <div className={`hidden md:grid gap-6 mb-16 items-stretch ${visiblePlans.length <= 3 ? 'md:grid-cols-3' : 'md:grid-cols-4'}`}>
            {visiblePlans.map(plan => <SharedPlanCard key={plan.id} plan={plan} popular={plan.id === resolvePopularId(visiblePlans)} onGetStarted={handleGetStarted} />)}
          </div>
        )}

        {/* Mobile carousel */}
        {!loading && visiblePlans.length > 0 && (
          <MobileCarousel plans={visiblePlans} />
        )}

        {/* FAQ */}
        <div className="max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl font-bold text-center mb-10 text-white">Frequently Asked Questions</h2>
          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-dark-700 rounded-xl overflow-hidden hover:border-dark-600 transition-colors">
                <button
                  type="button"
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  aria-expanded={openFaqIndex === index}
                  className="w-full px-6 py-4 flex items-center justify-between bg-dark-800 hover:bg-dark-700 transition-colors text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                >
                  <span className="font-semibold text-white text-sm sm:text-base">{faq.question}</span>
                  <ChevronDownIcon className={`w-5 h-5 text-gray-400 flex-shrink-0 ml-4 transition-transform duration-200 ${openFaqIndex === index ? 'rotate-180' : ''}`} />
                </button>
                {openFaqIndex === index && (
                  <div className="px-6 py-4 bg-dark-800/50 border-t border-dark-700">
                    <p className="text-gray-400 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center bg-dark-800 border border-dark-700 rounded-2xl p-10 mb-8">
          <h2 className="text-3xl font-bold mb-3 text-white">Still have questions?</h2>
          <p className="text-gray-400 mb-7 text-base max-w-xl mx-auto">
            Our support team is here to help you choose the right plan for your investment goals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button variant="outline" size="lg" className="min-w-[180px]">Contact Support</Button>
            </Link>
            <Link to="/register">
              <Button size="lg" className="min-w-[180px]">Get Started</Button>
            </Link>
          </div>
        </div>

        {/* Trust + disclaimer — single compliance block (reg IDs live in the site footer) */}
        <div className="bg-dark-800 border border-dark-700 rounded-2xl px-6 py-5 mb-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center text-sm">
            <div>
              <p className="font-semibold text-white">SEBI Registered</p>
              <p className="text-gray-400 text-xs mt-0.5">Research Analyst</p>
            </div>
            <div>
              <p className="font-semibold text-white">Fixed Fee Model</p>
              <p className="text-gray-400 text-xs mt-0.5">No profit-sharing or commissions</p>
            </div>
            <div>
              <p className="font-semibold text-white">No Execution Access</p>
              <p className="text-gray-400 text-xs mt-0.5">We advise, you decide</p>
            </div>
            <div>
              <p className="font-semibold text-white">Independent Research</p>
              <p className="text-gray-400 text-xs mt-0.5">Conflict-free recommendations</p>
            </div>
          </div>
          <div className="border-t border-dark-700 mt-4 pt-4">
            <p className="text-center text-xs text-gray-500 leading-relaxed">
              Advisory fees are fixed. Past performance is not indicative of future returns.
              Investment in securities is subject to market risks. Please read all related documents carefully.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PricingPage
